import { SpacesProvider, useSpaces } from "App/components/useEntities";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { where } from "firebase/firestore";
import { useAuth } from "App/components/Auth";
import { useMembershipFlags } from "App/components/MembershipFlagsContext";
import { useMyMemberships } from "App/components/Auth/MyMembershipsContext";
import { SpaceStub, membershipEntityToSpaceStub } from "./spaceStub";

/** Matches today's `limit: 150`; `loadMore` grows it in steps of 150. */
const LIBRARY_PAGE_SIZE = 150;

type LibrarySpacesContextValue = {
  /**
   * Under the new read model each entry is a `SpaceStub` built from a member
   * document, with no role data. A real `Space` satisfies `SpaceStub`, so the
   * legacy path serves the same consumers unchanged. See `useTopLevelSpaces`.
   */
  librarySpaces: SpaceStub[];
  librarySpacesLoading: boolean;
  totalLimitLibrarySpaces: number;
  loadMoreLibrarySpaces: () => void;
};

const LibrarySpacesContext = createContext<LibrarySpacesContextValue>({
  librarySpaces: [],
  librarySpacesLoading: true,
  totalLimitLibrarySpaces: 0,
  loadMoreLibrarySpaces: () => undefined,
});

const _LibrarySpacesContextProvider = ({
  membersV2Enabled,
  children,
}: {
  membersV2Enabled: boolean;
  children: React.ReactNode;
}) => {
  const [librarySpaces, setLibrarySpaces] = useState<SpaceStub[]>([]);
  const {
    entities: spaces,
    loading: legacyLoading,
    loadMore: legacyLoadMore,
    totalLimit: legacyTotalLimit,
  } = useSpaces();
  const { membersBySpaceId, loading: membershipsLoading } = useMyMemberships();
  const [membersTotalLimit, setMembersTotalLimit] =
    useState<number>(LIBRARY_PAGE_SIZE);

  const memberRows = useMemo(() => {
    if (!membersV2Enabled) return [];
    const rows: SpaceStub[] = [];
    membersBySpaceId.forEach((member) => {
      /*
       * `canRead` is the exact mirror of "this member's id is in
       * `space.readableByV2`", which is what the legacy query filtered on.
       */
      if (member.canRead !== true) return;
      rows.push(membershipEntityToSpaceStub(member));
    });
    return rows;
  }, [membersV2Enabled, membersBySpaceId]);

  useEffect(() => {
    if (membersV2Enabled) {
      /*
       * The same comparator and the same direction as the legacy line below.
       * We copied it and did not rewrite it: `a.name < b.name ? 1 : -1` sorts
       * in descending order, and it never returns 0, so it is not a stable
       * comparator. An exact reproduction is the point.
       *
       * The mutation is not the same, and that is fine: the legacy line
       * sorts `spaces` in place (it mutates the array `useEntities` handed
       * it), while `.slice(...)` here copies first. Nothing downstream
       * depends on the mutation, because `useEntities` calls `setEntities`
       * again on every snapshot, so a copy is strictly safer.
       */
      const sorted = memberRows
        .slice(0, membersTotalLimit)
        .sort((a, b) => (a.name < b.name ? 1 : -1));
      setLibrarySpaces(sorted);
      return;
    }
    const sortedSpaces = spaces.sort((a, b) => (a.name < b.name ? 1 : -1));
    setLibrarySpaces(sortedSpaces);
  }, [membersV2Enabled, spaces, memberRows, membersTotalLimit]);

  const value = useMemo(
    () => ({
      librarySpaces,
      librarySpacesLoading: membersV2Enabled
        ? membershipsLoading
        : legacyLoading,
      /*
       * We reproduce the limit-that-grows pattern on the client, because the
       * my-memberships stream is one 500-document stream, shared with the
       * permission engine, and `loadMore` must not re-issue it. The
       * `isMaxedOut` comparison in the Spaces tab of `Library/index.tsx`
       * (`librarySpaces.length === totalLimitLibrarySpaces`) still works,
       * unchanged.
       */
      loadMoreLibrarySpaces: membersV2Enabled
        ? () => setMembersTotalLimit((prev) => prev + LIBRARY_PAGE_SIZE)
        : legacyLoadMore,
      totalLimitLibrarySpaces: membersV2Enabled
        ? membersTotalLimit
        : legacyTotalLimit,
    }),
    [
      librarySpaces,
      membersV2Enabled,
      membershipsLoading,
      legacyLoading,
      legacyLoadMore,
      legacyTotalLimit,
      membersTotalLimit,
    ],
  );

  return (
    <LibrarySpacesContext.Provider value={value}>
      {children}
    </LibrarySpacesContext.Provider>
  );
};

export const LibrarySpacesContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuth();
  /*
   * The library read, moved to member documents. The member-document
   * equivalent is the my-memberships stream, filtered to `canRead`, and that
   * stream is already open for the permission engine. So this change opens
   * no new listener at all: it only derives new rows from data the client
   * already holds.
   *
   * Warning: there is one visible difference, and we name it. Today the
   * legacy query has no `orderBy`, so Firestore returns the 150 spaces with
   * the lowest `__name__` (a client-supplied UUID). The client then sorts
   * those 150 by name, descending. Under the flag the candidate pool
   * is the stream's 500, ordered by document path. For an account with
   * <=150 readable spaces the two sets are identical. Above 150 the flag
   * shows a different, and strictly larger, set. That is an improvement,
   * but also a change: a space that was invisible in the Library because
   * its UUID sorted late now appears. The team accepted this difference.
   */
  const { membersV2Enabled } = useMembershipFlags();

  const constraints = useMemo(() => {
    if (membersV2Enabled || !user?.id) return [];
    // PUBLIC_ID should not be used here as doing so will fetch all spaces that are public
    return [where("readableByV2", "array-contains-any", [user.id])];
  }, [user?.id, membersV2Enabled]);

  return (
    <SpacesProvider constraints={constraints} limit={LIBRARY_PAGE_SIZE}>
      <_LibrarySpacesContextProvider membersV2Enabled={membersV2Enabled}>
        {children}
      </_LibrarySpacesContextProvider>
    </SpacesProvider>
  );
};

export const useLibrarySpaces = () => useContext(LibrarySpacesContext);
